import { useState, useRef } from 'react'
import classnames from 'classnames/bind'
import dynamic from 'next/dynamic'
import { isEmpty } from 'lodash-es'
import { BigNumber } from 'bignumber.js'
import { useTranslation } from 'next-i18next'

// Libs
import { useAppSelector } from '@/redux/hooks'
import useFullScreen from '@/hooks/useFullScreen'
import { increaseShares } from '@/api/increaseShares'
import { useMutation } from 'react-query'
import { pxecPostMessages } from '@/utils/pxec/postMessages'

// Components
import Merchant from '../../sharedComponents/Merchant'
import Notification from '../../sharedComponents/Notification'
import Like from '../../sharedComponents/Like'
import MicRequest from '../../sharedComponents/MicRequest'
import ProductList from '../../sharedComponents/ProductList'
import MessageList from '../../sharedComponents/MessageList'
import ChatInput from '../../sharedComponents/ChatInput'
import Bag from '../../sharedComponents/Bag'
import Drawer from '@/components/common/Drawer'
import PhoneAction from '../../sharedComponents/PhoneAction'
import PrepareAction from '../../sharedComponents/PrepareAction'
import RoomCover from '../../sharedComponents/RoomCover'
import Playback from '../../sharedComponents/Playback'
import HandUpList from '../../sharedComponents/HandUpList'
import Icon from '@/assets/icons'
import MoreItem from '../../sharedComponents/MoreItem'
import MerchantInfo from '../../sharedComponents/MerchantInfo'
import NetworkStatus from '../../sharedComponents/NetworkStatus'
import SharePopup from '@/components/shared/SharePopup'
import Image from '@/components/common/Image'
import PortalWithPlayer from '@/components/common/PortalWithPlayer'
import Quality from '../../sharedComponents/Quality'

// Constants
import { CONNECTION_STATUS } from '@/constants/socketEvent'
import { GtmShareContentType } from '@/constants/gtmEvent'
import { ROOM_LIVE_TYPE } from '@/constants/liveRoom'

// Types
import { RoomProps } from '@/types/room'

// Styles
import styles from './style.module.scss'
import { isLiveMallClub } from '@/constants/systemType'

const Player = dynamic(() => import('@/components/common/Player'), {
  ssr: false,
})
const TickerTape = dynamic(() => import('@/components/pages/LiveRoom/sharedComponents/TickerTape'), {
  ssr: false,
})

const cx = classnames.bind(styles)

const Mobile = (props: RoomProps) => {
  const {
    srcWithQuality,
    merchantData,
    tickerTape,
    productList,
    roomPlacard,
    likes,
    isWebRTC,
    role,
    isHost = false,
    handleMicRequest,
    handleMicWaitingClick,
    isWaitingConnect,
    isMicConnecting,
    startTime = '',
    liveTime = '',
    liveCloseTime = '',
    endTime = '',
    actionData,
    isLive,
    isPast,
    isFuture,
    roomCoverData,
    isShowPlayer,
    isChatDisabled,
    hasPlayback,
    playbackData,
    liveType,
    isBySelf,
    setIsBySelf,
    isLessThenOneHour,
    handUpUserList,
    isPXEC,
  } = props

  const { t } = useTranslation()

  const [isProductListDrawerOpen, setIsProductListDrawerOpen] = useState(false)
  const [isMoreDrawerOpen, setIsMoreDrawerOpen] = useState(false)
  const [isMicConnectDrawerOpen, setIsMicConnectDrawerOpen] = useState(false)
  const [isStaticDrawerOpen, setIsStaticDrawerOpen] = useState(false)
  const [isQualityDrawerOpen, setIsQualityDrawerOpen] = useState(false)
  const [sharePopup, setSharePopup] = useState(false)

  const videoRef = useRef(null)
  const roomRef = useRef(null)
  const roomActionRef = useRef(null)

  const { isFullScreen } = useFullScreen()

  const socketData = useAppSelector((state) => state.socket)
  const { roomId, memberInfoId } = useAppSelector((state) => state.room.roomData)

  const productAmount = productList ? productList?.length : 0

  const {
    socket,
    messageList,
    notificationList,
    canHandUp,
    handUpList,
    micOnList,
    participantList,
    stateData,
    connectStatus,
    userToken,
    userCount,
    silentUserList,
  } = socketData
  const { likes: likesBySocket, views: viewsBySocket } = stateData

  const isWebRTCConnecting = connectStatus === CONNECTION_STATUS.CONNECTING

  const messageListOnlyChat = messageList.filter((item) => item.type === 'chat')
  const isReplay = liveType === ROOM_LIVE_TYPE.PAST

  const { mutate: increaseSharesMutation } = useMutation(increaseShares)

  return (
    <>
      <div className={cx('room-background')}>
        <Image src='/images/liveroom-background@2x.png' fill alt='live room background' style={{ objectFit: 'cover' }} />
      </div>
      <div id='room' className={cx('room')} ref={roomRef}>
        {isShowPlayer && !hasPlayback && (
          <Player videoRef={videoRef} src={srcWithQuality} isWebRTC={isWebRTC} role={role} isWebRTCConnecting={isWebRTCConnecting} />
        )}
        {!isLive && !isShowPlayer && !hasPlayback && <RoomCover {...roomCoverData} />}
        {hasPlayback && (
          <Playback
            {...playbackData}
            {...(isLiveMallClub && {
              bagComponent: (
                <div className={cx('playback-bag-wrapper')}>
                  <PortalWithPlayer isFullScreen={isFullScreen}>
                    <Bag
                      className={cx({ 'room-player-bag': isFullScreen })}
                      hanldeBagClick={() => setIsProductListDrawerOpen(true)}
                      count={productAmount}
                    />
                  </PortalWithPlayer>
                </div>
              ),
            })}
          />
        )}
        <div className={cx('room-wrapper')}>
          <div id='roomAction' className={cx('room-interact', 'no-select')}>
            <div className={cx('room-interact-info')}>
              <div className={cx('room-interact-info-row')}>
                <Merchant {...merchantData} dummyViews={viewsBySocket > 0 ? viewsBySocket : merchantData?.dummyViews} />

                {isWebRTC && <NetworkStatus className={cx('room-interact-network')} />}
              </div>

              {!isEmpty(tickerTape) && <TickerTape tickerTape={tickerTape} />}
            </div>
            <div className={cx('room-content')}>
              {isLive && (isHost || isMicConnecting) && (
                <PhoneAction
                  isHost={isHost}
                  userToken={userToken}
                  role={role}
                  isMicConnecting={isMicConnecting}
                  isWebRTCConnecting={isWebRTCConnecting}
                  canHandUp={canHandUp}
                  socket={socket}
                  {...actionData}
                />
              )}

              {isFuture && isShowPlayer && <PrepareAction {...actionData} />}

              <div className={cx('room-content-wrapper', { isPast })}>
                <Notification className={cx('room-content-notification')} notificationList={notificationList} />
                {(isLive || !isEmpty(messageListOnlyChat)) && (
                  <MessageList
                    socket={socket}
                    messageList={messageList.filter((item) => item.type === 'chat' || item.type === 'placard')}
                    roomPlacard={roomPlacard}
                    isBySelf={isBySelf}
                    setIsBySelf={setIsBySelf}
                    isHost={isHost}
                    silentUserList={silentUserList}
                  />
                )}
              </div>

              {canHandUp && !isHost && (
                <MicRequest
                  className={cx('room-content-mic')}
                  handleMicRequest={handleMicRequest}
                  handleMicWaitingClick={handleMicWaitingClick}
                  isWaitingConnect={isWaitingConnect}
                  isMicConnecting={isMicConnecting}
                />
              )}
            </div>

            {(isLive || (isPast && !isLiveMallClub)) && (
              <div className={cx('room-action')} ref={roomActionRef}>
                <ChatInput
                  className={cx('room-action-input')}
                  isChatDisabled={isChatDisabled}
                  setIsBySelf={setIsBySelf}
                  isHost={isHost}
                  hostId={memberInfoId}
                  roomId={roomId}
                />
                {isHost && isLive && (
                  <div className={cx('room-action-icon')} onClick={() => setIsMicConnectDrawerOpen(true)}>
                    <Icon.MicConnect />

                    {handUpUserList?.length > 0 && (
                      <div className={cx('room-action-icon-mic-count')}>
                        <span className={cx('room-action-icon-mic-count-number')}>{new BigNumber(handUpUserList?.length).toFormat()}</span>
                      </div>
                    )}
                  </div>
                )}

                <div className={cx('room-action-icon')} onClick={() => setIsMoreDrawerOpen(true)}>
                  <Icon.More />
                </div>

                <Like className={cx('room-action-like')} isHost={isHost} likes={likesBySocket > 0 ? likesBySocket : likes} />
                <PortalWithPlayer isFullScreen={isFullScreen}>
                  <Bag
                    className={cx({ 'room-player-bag': isFullScreen })}
                    hanldeBagClick={() => {
                      setIsProductListDrawerOpen(true)
                      if (isPXEC && roomId) {
                        pxecPostMessages.openProductList(
                          roomId,
                          productList.map((item) => (item as any)?.productUrl?.split('/').pop()),
                        )
                      }
                    }}
                    count={productAmount}
                  />
                </PortalWithPlayer>
              </div>
            )}
          </div>

          {isFuture && (
            <div className={cx('room-future')}>
              <Bag className={cx('room-future-bag')} hanldeBagClick={() => setIsProductListDrawerOpen(true)} count={productAmount} />
            </div>
          )}
        </div>

        {/* 更多功能 */}
        <Drawer
          className={cx('room-drawer')}
          open={isMoreDrawerOpen}
          direction='bottom'
          onClose={() => setIsMoreDrawerOpen(false)}
          lockBackgroundScroll
        >
          <div className={cx('room-drawer__more')}>
            <p className={cx('room-drawer-title')}>{t('LiveRoom_More')}</p>
            <div className={cx('room-drawer__more-list')}>
              {/* CAUTION: 暫時不開放分享功能，等待 live mall club 分享功能完成後再開放 */}
              {!isLiveMallClub && (
                <MoreItem
                  onClick={() => {
                    if (isPXEC && roomId) {
                      pxecPostMessages.shareRoom(roomId)
                      increaseSharesMutation({
                        roomId,
                      })
                      return
                    }
                    setSharePopup(true)
                  }}
                  className={cx('room-drawer__action-icon')}
                  text={t('Common_Share_Live')}
                  icon={Icon.MoreShare}
                />
              )}

              {!isHost && !isReplay && (
                <MoreItem
                  onClick={() => {
                    setIsQualityDrawerOpen(true)
                  }}
                  className={cx('room-drawer__action-icon')}
                  text={t('LiveRoom_Quality')}
                  icon={Icon.Setting}
                />
              )}
              {isHost && (
                <MoreItem
                  className={cx('room-drawer__action-icon')}
                  text={t('LiveRoom_Statistics')}
                  icon={Icon.MoreTrend}
                  onClick={() => setIsStaticDrawerOpen(true)}
                />
              )}
            </div>
          </div>
          <SharePopup isOpen={sharePopup} onCancel={() => setSharePopup(false)} gtmShareContentType={GtmShareContentType.CONTENT_TYPE_LIVEROOM} />
        </Drawer>

        {/* 商品列表 */}
        <PortalWithPlayer isFullScreen={isFullScreen}>
          <Drawer
            className={cx('room-drawer')}
            open={isProductListDrawerOpen}
            direction='bottom'
            onClose={() => setIsProductListDrawerOpen(false)}
            lockBackgroundScroll
          >
            <ProductList
              className={cx('room-list')}
              productList={productList}
              isLive={isLive}
              isHost={isHost}
              isLessThenOneHour={isLessThenOneHour}
              videoRef={videoRef}
            />
          </Drawer>
        </PortalWithPlayer>

        {/* 連麥列表 */}
        <Drawer
          className={cx('room-drawer')}
          open={isMicConnectDrawerOpen}
          direction='bottom'
          onClose={() => setIsMicConnectDrawerOpen(false)}
          lockBackgroundScroll
          style={{ height: '100%' }}
        >
          <div className={cx('room-mic-list')}>
            <p className={cx('room-drawer-title')}>{t('LiveRoom_MicListPopup_Title')}</p>
            <HandUpList micOnList={micOnList} handUpList={handUpList} canHandUp={canHandUp} participantList={participantList} />
          </div>
        </Drawer>

        {/* 即時數據 */}
        <Drawer
          open={isStaticDrawerOpen}
          direction='bottom'
          onClose={() => setIsStaticDrawerOpen(false)}
          lockBackgroundScroll
          style={{ height: '100%' }}
        >
          <div className={cx('room-drawer__static')}>
            <p className={cx('room-drawer-title')}>{t('LiveRoom_Statistics')}</p>

            <MerchantInfo
              {...merchantData}
              dummyViews={viewsBySocket > 0 ? viewsBySocket : merchantData?.dummyViews}
              startTime={startTime}
              liveTime={liveTime}
              liveCloseTime={liveCloseTime}
              endTime={endTime}
              userCount={userCount}
              isFuture={isFuture}
              isPast={isPast}
              liveType={liveType}
            />
          </div>
        </Drawer>

        {/* 畫質調整 */}
        <Drawer
          className={cx('room-drawer')}
          open={isQualityDrawerOpen}
          direction='bottom'
          onClose={() => setIsQualityDrawerOpen(false)}
          lockBackgroundScroll
        >
          <div className={cx('room-quality')}>
            <p className={cx('room-drawer-title')}>{t('LiveRoom_Quality')}</p>

            <Quality
              handleClose={() => {
                setIsQualityDrawerOpen(false)
                setIsMoreDrawerOpen(false)
              }}
            />
          </div>
        </Drawer>
      </div>
    </>
  )
}

export default Mobile
